import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import SelectedDesigns, { differentSize } from '../components/SelectedDesigns';
import Modal from '../components/Modal';
import SlotsManageTab from '../components/SlotsManageTab';
import PriceMatchTab from '../components/PriceMatchTab';
import CatalogCourierTab from '../components/CatalogCourierTab';
import DispatchIssueModal from '../components/DispatchIssueModal';
import { getBookingCheck, createPackBooked, getPackIssues } from '../api/catalogOrders';
import {
  getPackableAppointments, getPackAssignedAppointments,
  getInventoryForAppointment, lookupItemByBarcodeForPack, getSeenSkusForAppointment,
  getPackItems,
  createPack, cancelPack, updatePackItems, markDispatched,
  getDispatchedAppointments, getSetsInPack,
  startReversal, reversalCountCheck, reversalAccept, reversalScan, reversalEscalate,
  startAdminReversal, adminReversalComplete,
  getReversedItems, conditionCheck, restockAll, getPackers,
  getIroningItems, ironingClear, suggestPack, downloadComicGift
} from '../api/dispatch';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { retagUpload, getRetagItems, retagClear } from '../api/pricing';
import { getCategories, getSizes, getUseCases, useDispatchEtas } from '../api/meta';
import { getPredispatchBoard, listPackers, assignPackToPacker, getPackReview, resolvePack, getSearchQueue, searchQueueFound, searchQueueMarkMissing } from '../api/predispatch';
import { generateReceipt } from '../api/receipts';
import { allocatedInfo } from '../utils/allocated';
import { downloadHtml } from '../utils/download';
import { getDesignForRecategorize, recategorizeDesign } from '../api/recategorize';

// Actual selling price = lower of Zoddle Price and Saver Price (matches the
// effective-price convention used elsewhere, e.g. Customer Catalog).
function effectivePrice(item) {
  const zp = item?.zoddle_price;
  const sp = item?.saver_price;
  if (sp == null) return zp;
  return Math.min(zp, sp);
}

// ── Multi-select dropdown ───────────────────────────────────────────────────

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const allSel = options.length > 0 && options.every(o => selected.has(o));
  const summary = options.length === 0 ? '—'
    : allSel ? 'All' : (selected.size === 0 ? 'None' : `${selected.size}/${options.length}`);
  function toggle(o) {
    const next = new Set(selected);
    if (next.has(o)) next.delete(o); else next.add(o);
    onChange(next);
  }
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-xs font-medium border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-400 transition whitespace-nowrap">
        {label}: <span className="font-semibold text-gray-700">{summary}</span> <span className="text-gray-400">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 max-h-72 overflow-y-auto min-w-[12rem]">
            <div className="flex gap-3 mb-2 px-1">
              <button type="button" onClick={() => onChange(new Set(options))} className="text-xs text-zoddle-blue hover:underline">All</button>
              <button type="button" onClick={() => onChange(new Set())} className="text-xs text-gray-400 hover:underline">None</button>
            </div>
            {options.length === 0 && <p className="text-xs text-gray-400 px-1 py-1">No options</p>}
            {options.map(o => (
              <label key={o} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded">
                <input type="checkbox" checked={selected.has(o)} onChange={() => toggle(o)} />
                <span className="text-gray-700">{o}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Searchable multi-select (type to filter options, tick to select) ─────────

function SearchMultiSelect({ label, options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(o => !q || o.toLowerCase().includes(q));
  }, [options, query]);
  function toggle(o) {
    const next = new Set(selected);
    if (next.has(o)) next.delete(o); else next.add(o);
    onChange(next);
  }
  const summary = selected.size === 0 ? 'Any' : `${selected.size} selected`;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-xs font-medium border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-400 transition whitespace-nowrap">
        {label}: <span className="font-semibold text-gray-700">{summary}</span> <span className="text-gray-400">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-64 max-h-80 flex flex-col">
            <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder}
              className="border border-gray-200 rounded px-2 py-1 text-sm mb-2 focus:outline-none focus:border-zoddle-green" />
            {selected.size > 0 && (
              <button type="button" onClick={() => onChange(new Set())}
                className="text-xs text-gray-400 hover:underline mb-1 self-start">Clear ({selected.size})</button>
            )}
            <div className="overflow-y-auto">
              {filtered.length === 0 && <p className="text-xs text-gray-400 px-1 py-1">No matches</p>}
              {filtered.slice(0, 100).map(o => (
                <label key={o} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded">
                  <input type="checkbox" checked={selected.has(o)} onChange={() => toggle(o)} />
                  <span className="text-gray-700 truncate">{o}</span>
                </label>
              ))}
              {filtered.length > 100 && <p className="text-xs text-gray-400 px-1 py-1">+{filtered.length - 100} more — keep typing</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Item Picker Modal ───────────────────────────────────────────────────────

const PICKER_GENDERS = ['girl', 'boy', 'unisex'];

// Split a style_tags string into individual tags, stripping ":<n>%" profile suffixes.
function styleTagsOf(str) {
  if (!str) return [];
  return str.split(';').map(t => t.trim().replace(/:\s*\d+%?\s*$/, '')).filter(Boolean);
}

// Which canonical use-cases appear in a tag string (handles "party-wear" + "partywear:60%").
function matchUseCases(tagStr, allUseCases) {
  if (!tagStr) return [];
  const norm = String(tagStr).toLowerCase().replace(/-/g, '');
  return allUseCases.filter(u => norm.includes(u.toLowerCase().replace(/-/g, '')));
}

// Basket brand-card tint by brand price tier (full literal classes for Tailwind).
function tierCardClass(tier) {
  if (tier === 'Premium') return 'bg-red-100 border-red-200';   // light red
  if (tier === 'OPP') return 'bg-green-100 border-green-200';   // faint green
  return 'bg-gray-50 border-gray-200';                          // Mid / Unclassified / none
}

// Packing-grid section order — ethnic first, party second (matches the picked
// sidebar's order), then the remaining use-cases, "Other" last.
const USE_CASE_SECTION_ORDER = ['ethnic-wear', 'party-wear', 'winter-wear', 'sleep-wear', 'active-wear', 'casual-wear'];
function primaryUseCase(it, allUseCases) {
  const ucs = matchUseCases(it.use_case_tags, allUseCases);
  return USE_CASE_SECTION_ORDER.find(u => ucs.includes(u)) || null;
}
function sectionLabel(u) {
  return u ? u.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ') : 'Other';
}

// Picked-sidebar sort order: ethnic -> party -> other clothes -> shoes -> everything else.
function pickedSortPriority(it) {
  const tagStr = String(it.use_case_tags || '').toLowerCase().replace(/-/g, '');
  if (tagStr.includes('ethnicwear')) return 0;
  if (tagStr.includes('partywear')) return 1;
  if (it.category === 'footwear') return 3;
  if (it.category === 'accessories' || it.category === 'babycare') return 4;
  return 2; // other clothes: top, bottom, dress, set, innerwear, school
}

// Fix a design's gender / use-case live from the packing grid (core team + admin).
// Design-level: applies to every unit of the design; each change is logged.
function RecategorizeModal({ item, onClose, onSaved }) {
  const [info, setInfo] = useState(null);
  const [gender, setGender] = useState('');
  const [tags, setTags] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    getDesignForRecategorize(item.design_id)
      .then(d => { setInfo(d); setGender(d.gender); setTags(new Set(d.use_case_tags)); })
      .catch(e => setErr(e.response?.data?.detail || 'Could not load design'));
  }, [item.design_id]);
  const options = info ? [...info.use_cases, ...info.use_case_tags.filter(t => !info.use_cases.includes(t))] : [];
  const toggle = t => setTags(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await recategorizeDesign(item.design_id, gender, options.filter(t => tags.has(t)));
      onSaved(item.design_id, r.gender, r.use_case_tags);
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); setSaving(false); }
  };
  return (
    <Modal title="Fix gender / use-case" onClose={onClose}>
      {!info ? <p className="text-sm text-gray-500">{err || 'Loading…'}</p> : (
        <div className="space-y-4">
          <div className="flex gap-3 items-center">
            {item.image_url_1 && <img src={cldThumb(item.image_url_1, 150)} alt="" className="w-16 h-16 object-cover rounded-lg bg-gray-100" />}
            <div className="text-sm">
              <p className="font-semibold text-gray-800">Design {info.design_code} · {info.brand_name}</p>
              <p className="text-gray-500">{info.design_name}</p>
              <p className="text-xs text-amber-700 mt-1">Applies to all {info.units_with_us} unit{info.units_with_us !== 1 ? 's' : ''} of this design</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Gender</p>
            <div className="flex gap-2">
              {info.genders.map(g => (
                <button key={g} type="button" onClick={() => setGender(g)}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${gender === g ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-600'}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Use-case</p>
            <div className="flex flex-wrap gap-2">
              {options.map(t => (
                <label key={t} className="flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 rounded-lg px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={tags.has(t)} onChange={() => toggle(t)} />{t}
                </label>
              ))}
            </div>
          </div>
          {err && <p className="text-sm text-zoddle-pink">{err}</p>}
          <div className="flex gap-3">
            <button onClick={save} disabled={saving || tags.size === 0}
              className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={onClose} className="border border-gray-200 text-gray-600 text-sm font-semibold px-5 py-2 rounded-lg">Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ItemPickerModal({ appointment, onClose, onPacked, editPackId = null }) {
  const { auth } = useAuth();
  const canRecategorize = ['core_team', 'admin'].includes(auth?.access_level);
  const [recatItem, setRecatItem] = useState(null);
  const children = appointment.children || [];
  const [activeChildIdx, setActiveChildIdx] = useState(0);
  const activeChild = children[activeChildIdx] || {};
  // Website picks in a size other than the child's: the packer is told once when the pack screen opens.
  const childSizes = children.map(c => c.size).filter(Boolean);
  const oddSizes = differentSize(appointment.selected_designs, childSizes);
  const [sizeAlert, setSizeAlert] = useState(oddSizes.length > 0);
  // Active child's "other" preferences (price intentionally omitted — no field yet).
  const otherPrefs = [
    activeChild.use_case_pref && `Use: ${activeChild.use_case_pref}`,
    activeChild.style_pref && `Style: ${activeChild.style_pref}`,
    activeChild.avoid_tags && `Avoid: ${activeChild.avoid_tags}`,
  ].filter(Boolean).join(' · ');

  // Canonical option lists (single source: /meta)
  const [allSizes, setAllSizes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allUseCases, setAllUseCases] = useState([]);

  // Server-side fetch keys
  const [selSizes, setSelSizes] = useState(new Set());
  const [selGenders, setSelGenders] = useState(new Set());
  // Client-side refine
  const [selCategories, setSelCategories] = useState(new Set());
  const [selUseCases, setSelUseCases] = useState(new Set());
  const [deselColours, setDeselColours] = useState(new Set()); // colours the user unticked
  const [selBrands, setSelBrands] = useState(new Set());       // empty = any
  const [selStyleTags, setSelStyleTags] = useState(new Set()); // empty = any
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packing, setPacking] = useState(false);

  // Basket — keep full item objects so it survives re-fetches.
  const [selected, setSelected] = useState(new Set());
  const [selMeta, setSelMeta] = useState({}); // item_id -> item

  // After a recategorize, update every loaded unit of that design in place (grid +
  // basket) — no refetch, so a piece whose gender changed stays visible until the
  // next size/gender filter change.
  const applyRecat = (designId, gender, useCaseTags) => {
    const upd = it => (it.design_id === designId ? { ...it, gender, use_case_tags: useCaseTags } : it);
    setItems(prev => prev.map(upd));
    setSelMeta(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, upd(v)])));
    setRecatItem(null);
  };

  // Pack quality self-rating (1-5), asked only at initial packing (not edit). Nullable/skippable.
  const [packRating, setPackRating] = useState(null);

  // Auto-pack suggestion (Stage 3, AUTO_PACK_BUILD_CONTRACT.md).
  const [suggesting, setSuggesting] = useState(false);
  const [suggestMsg, setSuggestMsg] = useState(null);

  // SKUs already dispatched to this customer (completed/RTO) on a past appointment.
  // Fetched once per appointment — independent of size/gender filters.
  const [seenSkuIds, setSeenSkuIds] = useState(new Set());
  useEffect(() => {
    getSeenSkusForAppointment(appointment.appointment_id)
      .then(d => setSeenSkuIds(new Set(d.sku_ids)))
      .catch(() => setSeenSkuIds(new Set()));
  }, [appointment.appointment_id]);

  // Load canonical lists once; default-select categories + use-cases (all).
  useEffect(() => {
    getSizes().then(setAllSizes).catch(() => {});
    getCategories().then(cs => { setAllCategories(cs); setSelCategories(new Set(cs)); }).catch(() => {});
    getUseCases().then(us => { setAllUseCases(us); setSelUseCases(new Set(us)); }).catch(() => {});
  }, []);

  // Active child -> default fetch keys (its size; its gender + unisex).
  useEffect(() => {
    setSelSizes(new Set(activeChild.size ? [activeChild.size] : []));
    setSelGenders(new Set(activeChild.gender ? [activeChild.gender, 'unisex'] : ['unisex']));
    setSuggestMsg(null);
  }, [activeChildIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch items whenever the fetch keys (size / gender) change.
  const loadItems = useCallback(async () => {
    if (selSizes.size === 0 || selGenders.size === 0) { setItems([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const data = await getInventoryForAppointment(appointment.appointment_id, {
        sizes: Array.from(selSizes), genders: Array.from(selGenders),
      });
      setItems(data);
    } catch (e) { setError('Failed to load inventory'); }
    finally { setLoading(false); }
  }, [appointment.appointment_id, selSizes, selGenders]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Edit mode: pre-fill the basket with the pack's current items.
  useEffect(() => {
    if (!editPackId) return;
    (async () => {
      try {
        const data = await getPackItems(appointment.appointment_id);
        const its = data.items || [];
        setSelected(new Set(its.map(i => i.item_id)));
        setSelMeta(Object.fromEntries(its.map(i => [i.item_id, i])));
      } catch (e) { /* leave basket empty on failure */ }
    })();
  }, [editPackId, appointment.appointment_id]);

  // Catalog order: run the live check on the booked pieces and pre-select them. A booked
  // piece that sits in another ready pack comes back flagged in_other_pack (packing it
  // moves it over: Transfer). Pieces that are no longer available are listed for the packer.
  const isCatalog = appointment.order_type === 'catalog';
  const [bookingInfo, setBookingInfo] = useState(null);
  useEffect(() => {
    if (!isCatalog || editPackId) return;
    (async () => {
      try {
        const check = await getBookingCheck(appointment.appointment_id);
        setBookingInfo(check);
        const found = [];
        for (const b of check.booked || []) {
          try { found.push(await lookupItemByBarcodeForPack(b.zoddle_barcode, appointment.appointment_id)); }
          catch (e) { /* not packable right now: the packer sees it missing from the basket */ }
        }
        setSelected(prev => { const n = new Set(prev); found.forEach(it => n.add(it.item_id)); return n; });
        setSelMeta(prev => ({ ...prev, ...Object.fromEntries(found.map(it => [it.item_id, it])) }));
      } catch (e) {
        setBookingInfo({ error: e.response?.data?.detail || 'Could not check the booked pieces.' });
      }
    })();
  }, [isCatalog, editPackId, appointment.appointment_id]);

  // Colour options derive from loaded items; "selected" = options minus deselected.
  const colourOptions = useMemo(
    () => Array.from(new Set(items.map(i => i.color).filter(Boolean))).sort(),
    [items]
  );
  const brandOptions = useMemo(
    () => Array.from(new Set(items.map(i => i.brand_name).filter(Boolean))).sort(),
    [items]
  );
  const styleTagOptions = useMemo(() => {
    const s = new Set();
    items.forEach(i => styleTagsOf(i.style_tags).forEach(t => s.add(t)));
    return Array.from(s).sort();
  }, [items]);
  const colourSelected = useMemo(
    () => new Set(colourOptions.filter(c => !deselColours.has(c))),
    [colourOptions, deselColours]
  );
  const allColoursSelected = colourOptions.every(c => !deselColours.has(c));
  const allUseCasesSelected = allUseCases.length > 0 && allUseCases.every(u => selUseCases.has(u));
  const allCategoriesSelected = allCategories.length > 0 && allCategories.every(c => selCategories.has(c));

  function onColourChange(nextSel) {
    setDeselColours(new Set(colourOptions.filter(c => !nextSel.has(c))));
  }

  const visible = useMemo(() => {
    const pmin = parseFloat(priceMin), pmax = parseFloat(priceMax);
    return items.filter(it => {
      // price (zoddle_price)
      if (!Number.isNaN(pmin) && it.zoddle_price < pmin) return false;
      if (!Number.isNaN(pmax) && it.zoddle_price > pmax) return false;
      // brand (empty = any)
      if (selBrands.size > 0 && !selBrands.has(it.brand_name)) return false;
      // style tags (empty = any; else match any selected tag)
      if (selStyleTags.size > 0) {
        const tags = styleTagsOf(it.style_tags);
        if (!tags.some(t => selStyleTags.has(t))) return false;
      }
      // category (treat missing as match only when all categories selected)
      if (it.category) { if (!selCategories.has(it.category)) return false; }
      else if (!allCategoriesSelected) return false;
      // use-case (any selected; if none on item, match only when all selected)
      const ucs = matchUseCases(it.use_case_tags, allUseCases);
      if (ucs.length) { if (!ucs.some(u => selUseCases.has(u))) return false; }
      else if (!allUseCasesSelected) return false;
      // colour (missing -> match only when all selected)
      if (it.color) { if (deselColours.has(it.color)) return false; }
      else if (!allColoursSelected) return false;
      return true;
    });
  }, [items, selCategories, selUseCases, deselColours, allColoursSelected, allUseCasesSelected, allCategoriesSelected, selBrands, selStyleTags, priceMin, priceMax, allUseCases]);

  // Group into use-case sections, fixed order, "Other" last; skips empty sections.
  const sections = useMemo(() => {
    const groups = new Map();
    visible.forEach(it => {
      const key = primaryUseCase(it, allUseCases);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    });
    const ordered = [...USE_CASE_SECTION_ORDER, null]
      .filter(u => groups.has(u))
      .map(u => ({ key: u, label: sectionLabel(u), items: groups.get(u) }));
    return ordered;
  }, [visible, allUseCases]);

  // Picked sidebar order: ethnic -> party -> other clothes -> shoes -> everything
  // else, newest-picked-first within each group (stable sort keeps insertion order).
  const pickedSorted = useMemo(() => {
    const ids = Array.from(selected).reverse();
    return ids
      .map(id => selMeta[id])
      .filter(Boolean)
      .sort((a, b) => pickedSortPriority(a) - pickedSortPriority(b));
  }, [selected, selMeta]);

  function toggleItem(item) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(item.item_id)) next.delete(item.item_id); else next.add(item.item_id);
      return next;
    });
    setSelMeta(prev => ({ ...prev, [item.item_id]: item }));
  }
  function removeFromSelection(itemId) {
    setSelected(prev => { const n = new Set(prev); n.delete(itemId); return n; });
  }

  // ── Add-by-barcode (retained) ───────────────────────────────────────────--
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeBusy, setBarcodeBusy] = useState(false);
  const [barcodeMsg, setBarcodeMsg] = useState(null);

  async function handleAddByBarcode() {
    const raw = barcodeInput.trim();
    if (!raw) return;
    setBarcodeBusy(true); setBarcodeMsg(null);
    const uniqCodes = Array.from(new Set(raw.split(/[,\s\n]+/).map(s => s.trim()).filter(Boolean)));
    const byBarcode = new Map();
    items.forEach(it => { if (it.zoddle_barcode) byBarcode.set(it.zoddle_barcode, it); });
    Object.values(selMeta).forEach(it => { if (it.zoddle_barcode) byBarcode.set(it.zoddle_barcode, it); });

    const added = [], alreadySel = [], errs = [], newMeta = {}, newSel = new Set();
    for (const code of uniqCodes) {
      let item = byBarcode.get(code);
      if (!item) {
        try { item = await lookupItemByBarcodeForPack(code, appointment.appointment_id); byBarcode.set(code, item); }
        catch (e) { errs.push({ code, reason: e.response?.data?.detail || 'Lookup failed.' }); continue; }
      }
      if (selected.has(item.item_id) || newSel.has(item.item_id)) alreadySel.push(code);
      else { newSel.add(item.item_id); newMeta[item.item_id] = item; added.push(code); }
    }
    if (newSel.size > 0) {
      setSelected(prev => { const n = new Set(prev); newSel.forEach(id => n.add(id)); return n; });
      setSelMeta(prev => ({ ...prev, ...newMeta }));
    }
    const parts = [];
    if (added.length) parts.push(`Added ${added.length}`);
    if (alreadySel.length) parts.push(`${alreadySel.length} already selected`);
    if (errs.length) {
      const list = errs.slice(0, 5).map(e => `${e.code} (${e.reason})`).join('; ');
      parts.push(`${errs.length} failed: ${list}${errs.length > 5 ? '…' : ''}`);
    }
    setBarcodeMsg({ text: parts.join(' · '), isError: errs.length > 0 });
    if (errs.length === 0) setBarcodeInput('');
    setBarcodeBusy(false);
  }
  function onBarcodeKeyDown(e) { if (e.key === 'Enter') { e.preventDefault(); handleAddByBarcode(); } }

  // ── Set nudge: one-click add the coordinating partner piece ──────────────
  async function addPartner(item) {
    const bc = item?.set?.partner_barcode;
    if (!bc) return;
    let partner = items.find(it => it.zoddle_barcode === bc)
      || Object.values(selMeta).find(it => it.zoddle_barcode === bc);
    if (!partner) {
      try { partner = await lookupItemByBarcodeForPack(bc, appointment.appointment_id); }
      catch (e) { setBarcodeMsg({ text: `Set partner ${bc}: ${e.response?.data?.detail || 'lookup failed'}`, isError: true }); return; }
    }
    setSelected(prev => { const n = new Set(prev); n.add(partner.item_id); return n; });
    setSelMeta(prev => ({ ...prev, [partner.item_id]: partner }));
    setBarcodeMsg({ text: `Added set partner ${bc} (${item.set.set_code})`, isError: false });
  }

  // ── Suggest Pack (Stage 3): pre-fills the same basket as a manual click,
  // additive like add-by-barcode / addPartner — never replaces whatever's
  // already picked. Runs restock-all first server-side, but only for
  // admin-level callers (skipped otherwise); the endpoint decides that,
  // not this button.
  async function handleSuggest() {
    if (!activeChild.appointment_child_id) return;
    setSuggesting(true); setSuggestMsg(null);
    try {
      const result = await suggestPack(appointment.appointment_id, activeChild.appointment_child_id);
      if (result.error) { setSuggestMsg({ text: `Suggest Pack: ${result.error}`, isError: true }); return; }
      const fresh = (result.items || []).filter(it => !selected.has(it.item_id));
      if (fresh.length === 0) {
        setSuggestMsg({ text: 'Suggest Pack: nothing new to add.', isError: false });
        return;
      }
      setSelected(prev => { const n = new Set(prev); fresh.forEach(it => n.add(it.item_id)); return n; });
      setSelMeta(prev => {
        const next = { ...prev };
        fresh.forEach(it => { next[it.item_id] = it; });
        return next;
      });
      setSuggestMsg({ text: `Suggest Pack: added ${fresh.length} piece(s).`, isError: false });
    } catch (e) {
      setSuggestMsg({ text: e.response?.data?.detail || 'Suggest Pack failed.', isError: true });
    } finally {
      setSuggesting(false);
    }
  }

  async function handlePack() {
    if (selected.size === 0) return;
    if (!editPackId && packRating == null &&
        !window.confirm('Submit this pack without rating it out of 5?')) return;
    setPacking(true); setError(null);
    try {
      if (editPackId) await updatePackItems(editPackId, Array.from(selected));
      else await createPack(appointment.appointment_id, Array.from(selected), packRating);
      onPacked();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save pack'); }
    finally { setPacking(false); }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[96vw] h-[94vh] flex flex-col" onClick={e => e.stopPropagation()}>

        {isCatalog && !editPackId && (
          <div className="px-6 py-2 bg-zoddle-blue/10 border-b border-zoddle-blue/20 text-xs space-y-1">
            <p className="font-semibold text-zoddle-blue">
              Catalog order · the customer's booked pieces are pre-selected{bookingInfo?.booked?.length ? ` (${bookingInfo.booked.length} booked)` : ''}
            </p>
            {Array.from(selected).some(id => selMeta[id]?.in_other_pack) && (
              <p className="text-zoddle-pink font-semibold">A booked piece is still in another customer's pack. Clean that pack first (Dispatch tab → Clean pack); this pack can't be created until then.</p>
            )}
            {bookingInfo?.error && <p className="text-zoddle-pink">{typeof bookingInfo.error === 'string' ? bookingInfo.error : 'Could not check the booked pieces.'}</p>}
            {bookingInfo?.removed?.length > 0 && (
              <div>
                <p className="text-zoddle-pink font-semibold">
                  These booked pieces are no longer available. Add similar ones{bookingInfo.customer_phone ? ` or call the customer: ${bookingInfo.customer_phone}` : ''}.
                </p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {bookingInfo.removed.map(r => (
                    <div key={r.item_id} className="w-16 text-center">
                      {r.image_url ? <img src={cldThumb(r.image_url, 300)} alt="" className="w-16 h-16 object-cover rounded" />
                        : <div className="w-16 h-16 rounded bg-gray-200" />}
                      <p className="text-[10px] text-gray-600">{r.zoddle_barcode}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Header (title + filters) with preferences parallel on the right */}
        <div className="flex items-start gap-4 px-6 py-3 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <div>
              <h2 className="text-lg font-bold text-gray-800">{editPackId ? 'Edit Pack' : 'Pack Items'}</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {appointment.customer_name} · {appointment.scheduled_date}
                {appointment.zcp_appointment_uid && <> · UID <span className="font-mono font-bold text-gray-600">{appointment.zcp_appointment_uid}</span></>}
              </p>
            </div>
            {/* Filter bar */}
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {children.length > 1 && (
                <select value={activeChildIdx} onChange={e => setActiveChildIdx(Number(e.target.value))}
                  className="text-xs font-semibold border border-zoddle-blue text-zoddle-blue rounded-full px-3 py-1.5 focus:outline-none">
                  {children.map((c, i) => <option key={c.appointment_child_id || i} value={i}>Child {i + 1}: {c.size} {c.gender}</option>)}
                </select>
              )}
              <MultiSelect label="Size" options={allSizes} selected={selSizes} onChange={setSelSizes} />
              <MultiSelect label="Gender" options={PICKER_GENDERS} selected={selGenders} onChange={setSelGenders} />
              <MultiSelect label="Category" options={allCategories} selected={selCategories} onChange={setSelCategories} />
              <MultiSelect label="Use case" options={allUseCases} selected={selUseCases} onChange={setSelUseCases} />
              <MultiSelect label="Colour" options={colourOptions} selected={colourSelected} onChange={onColourChange} />
              <SearchMultiSelect label="Style tags" options={styleTagOptions} selected={selStyleTags} onChange={setSelStyleTags} placeholder="search style tags…" />
              <SearchMultiSelect label="Brand" options={brandOptions} selected={selBrands} onChange={setSelBrands} placeholder="search brands…" />
              <div className="flex items-center gap-1">
                <input type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)} placeholder="₹min"
                  className="text-xs border border-gray-200 rounded-full px-3 py-1.5 w-20 focus:outline-none focus:border-zoddle-green" />
                <span className="text-gray-400 text-xs">–</span>
                <input type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)} placeholder="₹max"
                  className="text-xs border border-gray-200 rounded-full px-3 py-1.5 w-20 focus:outline-none focus:border-zoddle-green" />
              </div>
              <span className="ml-auto text-xs text-gray-400">{visible.length} shown</span>
            </div>
          </div>
          {/* Right: preferences + close */}
          <div className="flex items-start gap-2 shrink-0">
            <div className="text-[11px] border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 max-w-[18rem]">
              <p className="font-semibold text-gray-500 mb-0.5">Preferences</p>
              <p className="text-gray-700"><span className="text-gray-400">Brands:</span> {activeChild.brand_pref || '—'}</p>
              <p className="text-gray-700"><span className="text-gray-400">Other:</span> {otherPrefs || '—'}</p>
            </div>
            <button onClick={handleSuggest} disabled={suggesting || !activeChild.appointment_child_id}
              className="bg-zoddle-blue text-white text-xs font-bold px-4 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0 self-center">
              {suggesting ? 'Suggesting…' : '✨ Suggest Pack'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>
        </div>

        {appointment.selected_designs && appointment.selected_designs.length > 0 && (
          <div className="px-6 py-2 border-b border-gray-100 bg-sky-50/40">
            <p className="text-xs font-semibold text-gray-500 mb-1">Parent picked these on the website <span className="font-normal text-gray-400">· bring them or similar ones</span></p>
            <SelectedDesigns designs={appointment.selected_designs} compact childSizes={childSizes} />
          </div>
        )}
        {sizeAlert && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] px-4" role="alertdialog" aria-labelledby="size-alert-title">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md px-6 py-5">
              <h3 id="size-alert-title" className="text-red-700 font-bold text-lg">Different size selected</h3>
              <p className="text-sm text-gray-600 mt-1">The parent picked {oddSizes.length === 1 ? 'this piece' : 'these pieces'} in a size other than the child's ({childSizes.join(' / ')}). Pack {oddSizes.length === 1 ? 'it' : 'them'} in the size shown.</p>
              <ul className="mt-3 space-y-1.5">
                {oddSizes.map(d => (
                  <li key={d.zoddle_barcode || d.code} className="text-sm text-gray-800">
                    {d.brand || 'Unknown'} <span className="font-mono text-gray-500">#{d.code}</span> · <b className="text-red-700">{d.size}</b>
                    {d.zoddle_barcode && <span className="font-mono text-gray-400"> · {d.zoddle_barcode}</span>}
                  </li>
                ))}
              </ul>
              <button onClick={() => setSizeAlert(false)} className="mt-4 w-full bg-zoddle-blue text-white text-sm font-bold py-2 rounded-lg hover:opacity-90">Got it</button>
            </div>
          </div>
        )}

        {/* Add by barcode (retained) */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <label className="text-xs font-semibold text-gray-500 whitespace-nowrap">Add by barcode:</label>
          <input type="text" value={barcodeInput} onChange={e => setBarcodeInput(e.target.value)} onKeyDown={onBarcodeKeyDown}
            placeholder="single or comma / space separated" disabled={barcodeBusy}
            className="flex-1 min-w-[14rem] max-w-md border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-zoddle-green disabled:bg-gray-100" />
          <button onClick={handleAddByBarcode} disabled={barcodeBusy || !barcodeInput.trim()}
            className="bg-zoddle-green text-white text-xs font-semibold px-4 py-1.5 rounded hover:opacity-90 disabled:opacity-50">
            {barcodeBusy ? 'Adding…' : 'Add'}
          </button>
          {barcodeMsg && <span className={`text-xs ${barcodeMsg.isError ? 'text-zoddle-pink' : 'text-gray-500'}`}>{barcodeMsg.text}</span>}
          {suggestMsg && <span className={`text-xs ${suggestMsg.isError ? 'text-zoddle-pink' : 'text-zoddle-blue'}`}>{suggestMsg.text}</span>}
        </div>

        {/* Grid + persistent "already picked" sidebar */}
        <div className="flex-1 flex min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {error && <p className="text-xs text-zoddle-pink mb-3">{error}</p>}
            {loading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Loading inventory…</div>
            ) : visible.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No items match these filters</div>
            ) : (
              <div className="space-y-5">
                {sections.map(sec => (
                  <div key={sec.key || 'other'}>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{sec.label} · {sec.items.length}</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {sec.items.map(item => {
                  const isSel = selected.has(item.item_id);
                  const partnerBc = item.set?.partner_barcode;
                  const partnerSel = partnerBc && Array.from(selected).some(id => selMeta[id]?.zoddle_barcode === partnerBc);
                  return (
                    <div key={item.item_id} onClick={() => toggleItem(item)}
                      className={`relative border-2 rounded-xl p-2 cursor-pointer transition ${isSel ? 'border-zoddle-green bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {isSel && <div className="absolute top-1.5 right-1.5 bg-zoddle-green text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold z-10">✓</div>}
                      {!isSel && seenSkuIds.has(item.sku_id) && <div title="Already sent to this customer (completed/RTO)" className="absolute top-1.5 right-1.5 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold z-10">✕</div>}
                      {item.set && <div className="absolute top-1.5 left-1.5 bg-zoddle-blue text-white text-[10px] leading-none rounded px-1 py-0.5 font-bold z-10" title={`Part of set ${item.set.set_code}`}>SET</div>}
                      <div className="relative">
                        {item.image_url_1
                          ? <img src={cldThumb(item.image_url_1, 300)} alt={item.design_name} className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                          : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                        {canRecategorize && item.design_id && (
                          <button type="button" title="Wrong gender / use-case? Fix it"
                            onClick={(e) => { e.stopPropagation(); setRecatItem(item); }}
                            className="absolute bottom-3 right-1 w-5 h-5 rounded-full bg-white/90 border border-gray-300 text-gray-500 text-xs font-bold leading-none flex items-center justify-center hover:bg-amber-400 hover:text-white hover:border-amber-400">!</button>
                        )}
                      </div>
                      <p className={`text-xs font-semibold text-gray-800 truncate ${item.low_recent_sendout ? 'underline decoration-dotted decoration-2' : ''}`}
                        title={item.low_recent_sendout ? 'Low recent sendout!' : undefined}>{item.brand_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.size} · {item.gender}
                        {item.category === 'footwear' && item.brand_size ? ` (${item.brand_size})` : ''}
                      </p>
                      <p className="text-xs text-gray-400 truncate"><span className="font-mono">{item.zoddle_barcode}</span>{item.design_name ? ` (${item.design_name})` : ''}</p>
                      <p className="text-xs font-bold text-zoddle-green mt-0.5">₹{effectivePrice(item)}</p>
                      {item.set && (
                        partnerSel
                          ? <p className="text-[10px] text-zoddle-blue font-semibold mt-1">✓ set partner added</p>
                          : item.set.partner_packable
                            ? <button onClick={(e) => { e.stopPropagation(); addPartner(item); }}
                                className="mt-1 w-full text-[10px] font-semibold text-zoddle-blue border border-zoddle-blue rounded py-0.5 hover:bg-blue-50 transition">
                                + add set partner
                              </button>
                            : <p className="text-[10px] text-gray-400 mt-1" title={`Partner ${partnerBc} is ${item.set.partner_status}`}>partner unavailable</p>
                      )}
                    </div>
                  );
                })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Already-picked sidebar — independent scroll, ~20% width */}
          <div className="w-1/5 shrink-0 border-l border-gray-100 overflow-y-auto px-3 py-4">
            <p className="text-xs font-semibold text-gray-700 mb-2 sticky top-0 bg-white">Picked ({selected.size})</p>
            {selected.size === 0 ? (
              <p className="text-xs text-gray-400">Nothing picked yet.</p>
            ) : (
              <div className="space-y-1.5">
                {pickedSorted.map(it => {
                  const id = it.item_id;
                  return (
                    <div key={id} onClick={() => removeFromSelection(id)} title="Click to remove"
                      className={`flex items-center gap-2 border rounded-lg p-1.5 cursor-pointer hover:opacity-80 transition ${tierCardClass(it.price_tier)}`}>
                      {it.image_url_1
                        ? <img src={cldThumb(it.image_url_1, 100)} alt="" className="w-9 h-9 object-cover rounded bg-gray-100 shrink-0" loading="lazy" />
                        : <div className="w-9 h-9 bg-gray-100 rounded shrink-0" />}
                      <div className="min-w-0">
                        <p className={`text-xs font-semibold text-gray-800 truncate ${it.low_recent_sendout ? 'underline decoration-dotted decoration-2' : ''}`}
                          title={it.low_recent_sendout ? 'Low recent sendout!' : undefined}>
                          {it.brand_name}{it.zoddle_barcode && <span className="font-normal text-gray-400"> ({it.zoddle_barcode})</span>}
                        </p>
                        <p className="text-xs text-zoddle-green font-bold">₹{effectivePrice(it)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer: pack */}
        <div className="border-t border-gray-100 px-6 py-3">
          {!editPackId && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-500">Rate this pack:</span>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button"
                  onClick={() => setPackRating(r => r === n ? null : n)}
                  className={`text-lg leading-none transition ${packRating && n <= packRating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`}
                  aria-label={`${n} star${n !== 1 ? 's' : ''}`}>★</button>
              ))}
              <span className="text-xs text-gray-400">{packRating ? `${packRating}/5` : 'optional'}</span>
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={handlePack} disabled={packing || selected.size === 0}
              className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              {packing ? 'Saving…' : (editPackId ? `Update pack (${selected.size})` : `Pack ${selected.size} Item${selected.size !== 1 ? 's' : ''}`)}
            </button>
            <button onClick={onClose} className="border border-gray-200 text-gray-600 text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-50 transition">Cancel</button>
          </div>
        </div>
        {recatItem && <RecategorizeModal item={recatItem} onClose={() => setRecatItem(null)} onSaved={applyRecat} />}
      </div>
    </div>
  );
}

// ── Sets pill (Dispatch tab): sets fully present in a pack ────────────────
function SetsPill({ packId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!packId) return;
    getSetsInPack(packId).then(d => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [packId]);
  if (!data || !data.count) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} title="Coordinating sets in this pack — bundle these pieces together"
        className="text-xs bg-blue-50 text-zoddle-blue border border-zoddle-blue font-semibold px-2 py-0.5 rounded-full hover:bg-blue-100 transition">
        {data.count} set{data.count !== 1 ? 's' : ''}
      </button>
      {open && (
        <Modal onClose={() => setOpen(false)}>
          <div className="p-4 w-full max-w-md">
            <h3 className="text-sm font-bold text-gray-700 mb-3">{data.count} set{data.count !== 1 ? 's' : ''} in this pack — bundle each pair together</h3>
            <div className="space-y-3">
              {data.sets.map((s, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-2">
                  <p className="text-[10px] text-gray-400 font-mono mb-1">{s.set_code}</p>
                  <div className="flex gap-2">
                    {s.pieces.map((p, j) => (
                      <div key={j} className="flex-1 text-center">
                        {p.image_url_1
                          ? <img src={cldThumb(p.image_url_1, 300)} alt="" className="w-full h-28 object-cover rounded bg-gray-100" />
                          : <div className="w-full h-28 bg-gray-100 rounded flex items-center justify-center text-gray-300 text-xs">no image</div>}
                        <p className="text-xs font-mono text-gray-600 mt-1">{p.zoddle_barcode}</p>
                        <p className="text-[10px] text-gray-400 truncate">{p.design_name} · {p.size}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Pack Items Detail Modal (read-only, used on Dispatch tab) ────────────

function PackItemsModal({ appointment, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getPackItems(appointment.appointment_id);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'Failed to load pack items.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [appointment.appointment_id]);

  const childInfo = appointment.children?.map(c => `${c.size} | ${c.gender}`).join(', ') || '—';

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Pack contents</h2>
            <p className="text-xs text-gray-400 mt-0.5">{appointment.customer_name} · {appointment.scheduled_date} · {childInfo}</p>
            {appointment.zcp_appointment_uid && (
              <p className="text-xs text-gray-500 mt-0.5">UID: <span className="font-mono font-bold">{appointment.zcp_appointment_uid}</span></p>
            )}
          </div>
          {data && (
            <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
              {data.item_count} item{data.item_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {error && <p className="text-xs text-zoddle-pink mb-3">{error}</p>}

        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading pack...</div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No items in this pack.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
            {data.items.map(item => (
              <div key={item.item_id}
                   className="border border-gray-200 rounded-xl p-2">
                {item.image_url_1
                  ? <img src={cldThumb(item.image_url_1, 300)} alt={item.design_name}
                         className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100"
                         loading="lazy" />
                  : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                <p className="text-xs font-semibold text-gray-800 truncate">{item.design_name || '—'}</p>
                <p className="text-xs text-gray-500">{item.size || '—'}</p>
                <p className="text-xs font-mono text-gray-400 truncate">{item.zoddle_barcode}</p>
                <p className="text-xs font-bold text-zoddle-green mt-1">₹{item.zoddle_price}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-gray-100">
          <button onClick={onClose}
            className="border border-gray-200 text-gray-600 text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-50 transition">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}


// ── Staff Reversal Modal ──────────────────────────────────────────────────

// Ironing regime handoff. Items landing on their 6th/12th/... showing were
// already marked in_ironing_queue by the reversal endpoint; this is a
// physical instruction + acknowledgement only and writes nothing.
function IroningHandoff({ items, onDone }) {
  const n = items.length;
  return (
    <div>
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
        <p className="text-sm font-bold text-orange-800 mb-1">
          Move {n} item{n !== 1 ? 's' : ''} to the ironing area
        </p>
        <p className="text-xs text-orange-700">
          These pieces have been out enough times to need ironing before they go
          back in a bin. They are already marked in the ironing queue — put them
          on the ironing rack, not in the restocking tray.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4 max-h-72 overflow-y-auto">
        {items.map(it => (
          <div key={it.item_id} className="border border-orange-200 rounded-lg overflow-hidden bg-white">
            {it.image_url_1
              ? <img src={cldThumb(it.image_url_1, 300)} alt={it.design_name || ''}
                  className="w-full aspect-square object-cover bg-gray-100" loading="lazy" />
              : <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-300 text-[10px]">no image</div>}
            <div className="p-1.5">
              <p className="text-[11px] font-bold text-gray-800 truncate">{it.brand_name || '—'}</p>
              <p className="text-[10px] text-gray-500 truncate">{it.design_name || '—'}</p>
              <p className="text-[10px] font-mono text-gray-600">{it.zoddle_barcode}</p>
              <p className="text-[10px] text-gray-400">{it.size || '—'} · shown {it.showing_number}&times;</p>
            </div>
          </div>
        ))}
      </div>
      <button onClick={onDone}
        className="w-full bg-orange-600 text-white text-sm font-bold py-2.5 rounded-lg hover:opacity-90 transition">
        Done — moved to ironing
      </button>
    </div>
  );
}


// A bolt-on rides on another order's bag (a -> b -> c), so the tag on the physical bag is the
// first order's, not this one's. Shown wherever the packer works a reversal so the bag can be
// matched to the row. Renders nothing for an order packed for itself.
function PackOriginNote({ origin, className = '' }) {
  if (!origin) return null;
  return (
    <p
      className={`text-[11px] font-semibold text-purple-700 ${className}`}
      title="A bolt-on rides on another order's bag. The tag on the physical bag is that order's."
    >
      Bolt-on · bag tagged <span className="font-mono">{origin.zcp_appointment_uid}</span>
      {origin.customer_name ? ` (${origin.customer_name})` : ''}
    </p>
  );
}

function ReversalModal({ appointment, onClose, onComplete }) {
  const [step, setStep] = useState('start');
  const [countInput, setCountInput] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [scannedBarcodes, setScannedBarcodes] = useState([]);
  const [scanError, setScanError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notLogged, setNotLogged] = useState(false);
  const [packers, setPackers] = useState([]);
  const [selectedPackerId, setSelectedPackerId] = useState('');
  const [ironingItems, setIroningItems] = useState([]);

  async function handleStart() {
    setLoading(true); setError(null); setNotLogged(false);
    try {
      await startReversal(appointment.appointment_id);
      setStep('count');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === 'order_not_logged') {
        setNotLogged(true);
      } else {
        setError((detail && detail.message) || detail || 'Failed to start');
      }
    }
    finally { setLoading(false); }
  }

  function goToAssignPacker() {
    setStep('assign-packer');
    getPackers().then(setPackers).catch(() => setPackers([]));
  }

  async function handleCountSubmit() {
    if (!countInput) return;
    setLoading(true); setError(null);
    try {
      const res = await reversalCountCheck(appointment.appointment_id, parseInt(countInput));
      if (res.match) {
        goToAssignPacker();
      } else {
        setStep('scan');
      }
    } catch (e) { setError(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }

  function addBarcode() {
    const b = scanInput.trim().toUpperCase();
    if (!b || scannedBarcodes.includes(b)) { setScanInput(''); return; }
    setScannedBarcodes(prev => [...prev, b]);
    setScanInput('');
  }

  async function handleScanSubmit() {
    setLoading(true); setScanError(null);
    try {
      const res = await reversalScan(appointment.appointment_id, scannedBarcodes);
      if (res.all_accounted) {
        goToAssignPacker();
      } else {
        setScanError('Some items are missing or incorrect. Please try again.');
      }
    } catch (e) { setScanError(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }

  async function handleAssignPacker() {
    if (!selectedPackerId) return;
    setLoading(true); setError(null);
    try {
      const res = await reversalAccept(appointment.appointment_id, selectedPackerId);
      const toIron = res?.ironing_items || [];
      if (toIron.length) { setIroningItems(toIron); setStep('ironing'); return; }
      onComplete('success');
    } catch (e) { setError(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }

  async function handleEscalate() {
    setLoading(true);
    try {
      await reversalEscalate(appointment.appointment_id, [], 'Escalated by staff');
      setStep('escalated');
    } catch (e) { setScanError(e.response?.data?.detail || 'Failed to escalate'); }
    finally { setLoading(false); }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-md">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Process Reversal</h2>
        <p className="text-xs text-gray-400 mb-4">
          {appointment.customer_name}
          {appointment.zcp_appointment_uid && <> · <span className="font-mono font-bold text-gray-700">{appointment.zcp_appointment_uid}</span></>}
        </p>
        <PackOriginNote origin={appointment.pack_origin} className="-mt-3 mb-4" />

        {error && <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-xs text-zoddle-pink">{error}</div>}

        {notLogged && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold text-yellow-800 mb-1">Order not logged.</p>
            <p className="text-xs text-yellow-700 mb-3">Please log RTO or DOL in the orders sheet, then retry.</p>
            <button onClick={handleStart} disabled={loading}
              className="w-full bg-zoddle-blue text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        )}

        {step === 'start' && !notLogged && (
          <div>
            <p className="text-sm text-gray-600 mb-6">Click Start Reversal to begin the returns process.</p>
            <button onClick={handleStart} disabled={loading}
              className="w-full bg-zoddle-blue text-white text-sm font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              {loading ? 'Starting...' : 'Start Reversal'}
            </button>
          </div>
        )}

        {step === 'count' && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-4">How many items do you have?</p>
            <div className="flex gap-3">
              <input type="number" value={countInput} onChange={e => setCountInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCountSubmit(); }}
                placeholder="Enter count"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
              <button onClick={handleCountSubmit} disabled={loading || !countInput}
                className="bg-zoddle-green text-white text-sm font-semibold px-4 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                {loading ? '...' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        {step === 'scan' && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Scan each item barcode</p>
            <p className="text-xs text-gray-400 mb-3">{scannedBarcodes.length} scanned</p>
            <div className="flex gap-2 mb-3">
              <input type="text" value={scanInput}
                onChange={e => setScanInput(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === 'Enter') addBarcode(); }}
                placeholder="Scan barcode e.g. ZOD12345678"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
              <button onClick={addBarcode} className="bg-zoddle-green text-white text-sm font-semibold px-4 rounded-lg hover:opacity-90 transition">Add</button>
            </div>
            <div className="space-y-1 max-h-36 overflow-y-auto mb-4">
              {scannedBarcodes.map(b => (
                <div key={b} className="flex items-center gap-2 bg-green-50 border border-green-100 rounded px-3 py-1">
                  <span className="text-xs font-mono text-gray-700 flex-1">{b}</span>
                  <button onClick={() => setScannedBarcodes(p => p.filter(x => x !== b))} className="text-gray-300 hover:text-zoddle-pink text-xs">✕</button>
                </div>
              ))}
            </div>
            {scanError && (
              <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">
                <p className="text-xs text-zoddle-pink">{scanError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={handleScanSubmit} disabled={loading || scannedBarcodes.length === 0}
                className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                {loading ? 'Checking...' : 'Verify & Accept'}
              </button>
              {scanError && (
                <button onClick={handleEscalate} disabled={loading}
                  className="border border-red-200 text-zoddle-pink text-xs font-semibold px-3 py-2 rounded-lg hover:bg-red-50 transition">
                  Escalate
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'assign-packer' && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Who's reracking this?</p>
            <p className="text-xs text-gray-400 mb-4">All items go into the restock queue tagged to this packer.</p>
            <select value={selectedPackerId} onChange={e => setSelectedPackerId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-zoddle-green">
              <option value="">Select a packer...</option>
              {packers.map(p => <option key={p.staff_id} value={p.staff_id}>{p.full_name}</option>)}
            </select>
            <button onClick={handleAssignPacker} disabled={loading || !selectedPackerId}
              className="w-full bg-zoddle-green text-white text-sm font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              {loading ? 'Finishing...' : 'Confirm & Complete Reversal'}
            </button>
          </div>
        )}

        {step === 'ironing' && (
          <IroningHandoff items={ironingItems} onDone={() => onComplete('success')} />
        )}

        {step === 'escalated' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-sm font-bold text-yellow-800 mb-2">Admin has been notified.</p>
            <p className="text-xs text-yellow-700">Please keep all items in the pack in the admin check area.</p>
            <button onClick={onClose} className="mt-4 w-full border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-lg hover:bg-gray-50 transition">Close</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Admin Reversal Modal ──────────────────────────────────────────────────

function AdminReversalModal({ appointment, onClose, onComplete }) {
  const [packItems, setPackItems] = useState([]);
  const [ironingItems, setIroningItems] = useState([]);
  const [presentIds, setPresentIds] = useState(new Set());
  const [commentary, setCommentary] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notLogged, setNotLogged] = useState(false);
  const [packers, setPackers] = useState([]);
  const [selectedPackerId, setSelectedPackerId] = useState('');

  const loadAdminReversal = useCallback(() => {
    setLoading(true); setError(null); setNotLogged(false);
    // start-admin-reversal syncs sales, gates on RTO/DOL log, returns items still in pack
    startAdminReversal(appointment.appointment_id)
      .then(data => {
        setPackItems(data.items || []);
        setPresentIds(new Set()); // all unselected by default
      })
      .catch(e => {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail?.error === 'order_not_logged') {
          setNotLogged(true);
        } else {
          setError((detail && detail.message) || detail || 'Failed to load pack items');
        }
      })
      .finally(() => setLoading(false));
  }, [appointment.appointment_id]);

  useEffect(() => { loadAdminReversal(); }, [loadAdminReversal]);
  useEffect(() => { getPackers().then(setPackers).catch(() => setPackers([])); }, []);

  function togglePresent(itemId) {
    setPresentIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  }

  async function handleConfirm() {
    setSubmitting(true); setError(null);
    try {
      const missingItems = packItems.filter(i => !presentIds.has(i.item_id));
      const missingBarcodes = missingItems.map(i => i.zoddle_barcode).filter(Boolean);
      const res = await adminReversalComplete(
        appointment.appointment_id,
        missingBarcodes,
        commentary || 'Admin reversal — items confirmed',
        selectedPackerId
      );
      const toIron = res?.ironing_items || [];
      if (toIron.length) { setIroningItems(toIron); return; }
      onComplete();
    } catch (e) { setError(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  }

  const presentCount = presentIds.size;
  const missingCount = packItems.length - presentCount;

  // Search-to-mark-present: suggestions from the 4th char, matching anywhere
  // in the barcode (tags are often read from the end). An exact match — a
  // scanner or a fully typed barcode — is added immediately.
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const q = query.trim();
  const inPackMatches = q.length >= 4 ? packItems.filter(i => (i.zoddle_barcode || '').includes(q)) : [];
  const suggestions = inPackMatches.filter(i => !presentIds.has(i.item_id)).slice(0, 8);
  const presentItems = packItems.filter(i => presentIds.has(i.item_id));
  const leftItems = packItems.filter(i => !presentIds.has(i.item_id));

  function addPresent(item) {
    setPresentIds(prev => new Set(prev).add(item.item_id));
    setQuery('');
  }

  function onQueryChange(v) {
    const exact = packItems.find(i => i.zoddle_barcode === v.trim());
    if (exact && !presentIds.has(exact.item_id)) { addPresent(exact); return; }
    setQuery(v);
  }

  function onQueryKeyDown(e) {
    if (e.key === 'Enter' && suggestions.length === 1) { e.preventDefault(); addPresent(suggestions[0]); }
  }

  function copyLeft() {
    const uid = appointment.zcp_appointment_uid || appointment.customer_name || '';
    const text = [`Please find these pieces (${uid}):`,
      ...leftItems.map(i => `${i.zoddle_barcode} — ${i.design_name || ''} ${i.size || ''}`.trim())].join('\n');
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  const thumb = item => item.image_url_1
    ? <img src={cldThumb(item.image_url_1, 100)} alt="" className="w-9 h-9 object-cover rounded bg-gray-100 shrink-0" loading="lazy" />
    : <div className="w-9 h-9 rounded bg-gray-100 shrink-0" />;

  // Reversal is already committed at this point — the handoff replaces the
  // body rather than sitting alongside it, and onComplete only fires on ack.
  if (ironingItems.length) {
    return (
      <Modal onClose={onClose}>
        <div className="p-6 w-full max-w-lg max-h-screen overflow-y-auto">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Reversal complete</h2>
          <IroningHandoff items={ironingItems} onDone={onComplete} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-xl max-h-screen overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Admin Reversal</h2>
        <p className="text-xs text-gray-400 mb-4">
          {appointment.customer_name}
          {appointment.zcp_appointment_uid && <> · <span className="font-mono font-bold text-gray-700">{appointment.zcp_appointment_uid}</span></>}
        </p>
        <PackOriginNote origin={appointment.pack_origin} className="-mt-3 mb-4" />
        <p className="text-xs text-gray-500 mb-4">Scan or type each piece you can physically see. Anything left will be marked as missing.</p>

        {error && <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-xs text-zoddle-pink">{error}</div>}

        {notLogged && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold text-yellow-800 mb-1">Order not logged.</p>
            <p className="text-xs text-yellow-700 mb-3">Please log RTO or DOL in the orders sheet, then retry.</p>
            <button onClick={loadAdminReversal} disabled={loading}
              className="w-full bg-zoddle-blue text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        )}

        {!notLogged && (loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Syncing sales and loading items...</div>
        ) : packItems.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">No items remaining in pack (all sold).</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500">{packItems.length} items in pack</p>
              <div className="flex gap-2 text-xs">
                <span className="text-green-600 font-medium">{presentCount} present</span>
                {missingCount > 0 && <span className="text-zoddle-pink font-medium">{missingCount} missing</span>}
              </div>
            </div>
            <div className="relative mb-4">
              <input autoFocus value={query} onChange={e => onQueryChange(e.target.value)} onKeyDown={onQueryKeyDown}
                placeholder="Scan or type barcode (4+ digits)…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
              {q.length >= 4 && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-72 overflow-y-auto">
                  {suggestions.map(item => (
                    <div key={item.item_id} onClick={() => addPresent(item)}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-green-50">
                      {thumb(item)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono font-bold text-gray-800">{item.zoddle_barcode}</p>
                        <p className="text-xs text-gray-500 truncate">{item.design_name} — {item.size}</p>
                      </div>
                      <span className="text-xs text-zoddle-green font-semibold">+ Add</span>
                    </div>
                  ))}
                  {suggestions.length === 0 && (
                    <p className="px-3 py-2 text-xs text-zoddle-pink">
                      {inPackMatches.length ? 'Already added.' : 'Not in this pack.'}
                    </p>
                  )}
                </div>
              )}
            </div>

            {presentItems.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-green-700 mb-1">Present ({presentItems.length}) — tap to undo</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {presentItems.map(item => (
                    <div key={item.item_id} onClick={() => togglePresent(item.item_id)}
                      className="flex items-center gap-3 rounded-lg px-3 py-1.5 cursor-pointer border bg-green-50 border-green-200 hover:border-green-300">
                      {thumb(item)}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono font-bold text-gray-800">{item.zoddle_barcode}</p>
                        <p className="text-xs text-gray-500 truncate">{item.design_name} — {item.size}</p>
                      </div>
                      <span className="text-gray-400 text-sm">×</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {leftItems.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-zoddle-pink">Left ({leftItems.length}) — will be marked missing</p>
                  <button onClick={copyLeft} className="text-xs text-zoddle-blue font-semibold hover:underline">
                    {copied ? 'Copied!' : 'Copy barcodes'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto">
                  {leftItems.map(item => (
                    <div key={item.item_id} onClick={() => togglePresent(item.item_id)} title="Mark present"
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer border bg-gray-50 border-gray-200 hover:border-gray-300">
                      {thumb(item)}
                      <div className="min-w-0">
                        <p className="text-xs font-mono font-bold text-gray-800">{item.zoddle_barcode}</p>
                        <p className="text-[11px] text-gray-500 truncate">{item.design_name} — {item.size}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {missingCount > 0 && (
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Commentary on missing items</label>
                <textarea value={commentary} onChange={e => setCommentary(e.target.value)} rows={2}
                  placeholder="Describe what happened..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green resize-none" />
              </div>
            )}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Who's reracking this?</label>
              <select value={selectedPackerId} onChange={e => setSelectedPackerId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green">
                <option value="">Select a packer...</option>
                {packers.map(p => <option key={p.staff_id} value={p.staff_id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={handleConfirm} disabled={submitting || presentIds.size === 0 || !selectedPackerId}
                className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                {submitting ? 'Processing...' : `Confirm — ${presentCount} present${missingCount ? ` · ${missingCount} will be marked missing` : ""}`}
              </button>
              <button onClick={onClose} className="border border-gray-200 text-gray-600 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-50 transition">Cancel</button>
            </div>
          </>
        ))}
      </div>
    </Modal>
  );
}

// ── Restock Tab ───────────────────────────────────────────────────────────

function RestockTab() {
  const { auth } = useAuth();
  const isAdmin = auth?.access_level === 'admin';
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [packers, setPackers] = useState([]);
  const [packerFilter, setPackerFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [restockingAll, setRestockingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const data = await getReversedItems(search, packerFilter); setItems(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, packerFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getPackers().then(setPackers).catch(() => setPackers([])); }, []);

  async function handleRestockAll() {
    if (!window.confirm(`Send all reversed items to their suggested bins (condition = good)? Items with no available bin will be skipped.`)) return;
    setRestockingAll(true);
    try {
      const res = await restockAll();
      alert(`Restocked ${res.restocked} item(s).` + (res.skipped ? ` ${res.skipped} skipped (no available bin) — still in the queue.` : ''));
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Restock all failed'); }
    finally { setRestockingAll(false); }
  }

  async function handleAction(item, action) {
    setProcessing(item.item_id);
    try {
      const binId = action === 'bin' ? item.suggested_bin?.bin_id : null;
      if (action === 'bin' && !binId) { alert('No suggested bin available'); setProcessing(null); return; }
      await conditionCheck(item.item_id, action, binId);
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input type="text" placeholder="Search by barcode or name..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green w-64" />
        <select value={packerFilter} onChange={e => setPackerFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green">
          <option value="">All packers</option>
          {packers.map(p => <option key={p.staff_id} value={p.staff_id}>{p.full_name}</option>)}
        </select>
        <button onClick={load} className="text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
        <span className="ml-auto text-xs text-gray-400">{items.length} items awaiting restock</span>
        {isAdmin && (
          <button onClick={handleRestockAll} disabled={restockingAll || items.length === 0}
            className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
            {restockingAll ? 'Restocking…' : 'Restock all'}
          </button>
        )}
      </div>
      {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      : items.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No items awaiting restock</div>
      : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Item</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Barcode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Suggested Bin</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Assigned to</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.item_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.image_url_1 ? <img src={cldThumb(item.image_url_1, 300)} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{item.design_name}</p>
                        <p className="text-xs text-gray-400">{item.color}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.zoddle_barcode}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{item.size}</td>
                  <td className="px-4 py-3">
                    {item.suggested_bin ? <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{item.suggested_bin.bin_label}</span> : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{item.restock_assigned_to || <span className="text-gray-300">Unassigned</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleAction(item, 'bin')} disabled={processing === item.item_id || !item.suggested_bin}
                        className="text-xs bg-zoddle-green text-white font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                        {processing === item.item_id ? '...' : '→ Bin'}
                      </button>
                      <button onClick={() => handleAction(item, 'ironing')} disabled={processing === item.item_id}
                        className="text-xs bg-yellow-400 text-white font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                        Ironing
                      </button>
                      <button onClick={() => handleAction(item, 'damaged')} disabled={processing === item.item_id}
                        className="text-xs bg-red-100 text-zoddle-pink font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                        Damaged
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Search Queue Tab (core_team + admin) ───────────────────────────────────

function SearchQueueTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await getSearchQueue()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleFound(item) {
    setProcessing(item.item_id);
    try {
      await searchQueueFound(item.item_id);
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  }

  async function handleMarkMissing(item) {
    if (!window.confirm(`Mark ${item.zoddle_barcode} as missing? This raises a discrepancy.`)) return;
    setProcessing(item.item_id);
    try {
      await searchQueueMarkMissing(item.item_id);
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={load} className="text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
        <span className="ml-auto text-xs text-gray-400">{items.length} items in search queue</span>
      </div>
      {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      : items.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No items in search queue</div>
      : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Item</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Barcode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">ZRP</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">In search from</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Allocated to</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.item_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.image_url_1 ? <img src={cldThumb(item.image_url_1, 300)} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                      <p className="text-xs font-semibold text-gray-800">{item.design_name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.zoddle_barcode}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{item.size}</td>
                  <td className="px-4 py-3 text-xs font-bold text-zoddle-green">₹{item.zoddle_price}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{item.in_search_since?.slice(0,10) || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{item.assigned_packer_name || <span className="text-gray-300">Unassigned</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleFound(item)} disabled={processing === item.item_id}
                        className="text-xs bg-zoddle-green text-white font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                        Found
                      </button>
                      <button onClick={() => handleMarkMissing(item)} disabled={processing === item.item_id}
                        className="text-xs bg-red-100 text-zoddle-pink font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                        Mark missing
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Generate Invoice Tab ────────────────────────────────────────────────────

const WHATSAPP_RECEIPT_MSG = "Thanks for shopping with Skipzy! Please find attached your tax invoice!";

function GenerateReceiptTab() {
  const [uid, setUid] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null); // { html, filename, phone, customer_name, invoice_number }

  async function handleGenerate() {
    if (!uid.trim()) return;
    setLoading(true); setError(''); setReceipt(null);
    try {
      setReceipt(await generateReceipt(uid.trim()));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate receipt');
    } finally {
      setLoading(false);
    }
  }

  function handleDownload() {
    downloadHtml(receipt.html, receipt.filename);
  }

  function handleSendWhatsapp() {
    // WhatsApp Web can't attach a file via link — auto-download so it's one click
    // away in Downloads, then open the chat with the message pre-filled.
    downloadHtml(receipt.html, receipt.filename);
    const digits = (receipt.phone || '').replace(/\D/g, '');
    const withCountryCode = digits.length === 10 ? `91${digits}` : digits;
    const url = `https://web.whatsapp.com/send?phone=${withCountryCode}&text=${encodeURIComponent(WHATSAPP_RECEIPT_MSG)}`;
    window.open(url, '_blank');
  }

  return (
    <div className="max-w-md">
      <p className="text-sm text-gray-500 mb-4">
        Enter the Appointment UID for a completed order to generate its Tax Invoice receipt.
      </p>
      <div className="flex gap-2">
        <input value={uid} onChange={e => setUid(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleGenerate()}
          placeholder="Appointment UID (e.g. SF3427)"
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
        <button onClick={handleGenerate} disabled={loading || !uid.trim()}
          className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
          {loading ? 'Generating...' : 'Generate'}
        </button>
      </div>
      {error && <p className="text-xs text-zoddle-pink mt-3 font-medium">{error}</p>}
      {receipt && (
        <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-800">{receipt.invoice_number}</p>
          <p className="text-xs text-gray-400 mb-3">{receipt.customer_name} · {receipt.phone || 'no phone on file'}</p>
          <div className="flex gap-2">
            <button onClick={handleDownload}
              className="text-xs bg-gray-100 text-gray-700 font-semibold px-3 py-2 rounded-lg hover:bg-gray-200 transition">
              ⬇ Download
            </button>
            <button onClick={handleSendWhatsapp} disabled={!receipt.phone}
              className="text-xs bg-zoddle-green text-white font-semibold px-3 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              Send on WhatsApp
            </button>
          </div>
          {!receipt.phone && <p className="text-xs text-gray-400 mt-2">No phone number on file — can't open WhatsApp.</p>}
        </div>
      )}
    </div>
  );
}

// ── Ironing Tab ───────────────────────────────────────────────────────────

function IroningTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const data = await getIroningItems(search); setItems(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  async function handleClear(item) {
    const binId = item.suggested_bin?.bin_id;
    if (!binId) { alert('No suggested bin available'); return; }
    setProcessing(item.item_id);
    try {
      await ironingClear(item.item_id, binId);
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input type="text" placeholder="Search by barcode or name..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green w-64" />
        <button onClick={load} className="text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
        <span className="ml-auto text-xs text-gray-400">{items.length} items in ironing queue</span>
      </div>
      {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      : items.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">Ironing queue is empty</div>
      : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Item</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Barcode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Suggested Bin</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.item_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.image_url_1 ? <img src={cldThumb(item.image_url_1, 300)} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{item.design_name}</p>
                        <p className="text-xs text-gray-400">{item.color}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.zoddle_barcode}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{item.size}</td>
                  <td className="px-4 py-3">
                    {item.suggested_bin ? <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{item.suggested_bin.bin_label}</span> : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleClear(item)} disabled={processing === item.item_id || !item.suggested_bin}
                      className="text-xs bg-zoddle-green text-white font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                      {processing === item.item_id ? '...' : 'Done → Bin'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Retagging Tab ─────────────────────────────────────────────────────────

function downloadCsv(filename, headers, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  (rows || []).forEach(r => lines.push(headers.map(h => esc(r[h])).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function RetaggingTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await getRetagItems(search)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load]);

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    try {
      const res = await retagUpload(file);
      setResult(res); setFile(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  }

  async function handleClear(item) {
    const binId = item.suggested_bin?.bin_id;
    if (!binId) { alert('No suggested bin available'); return; }
    setProcessing(item.item_id);
    try {
      await retagClear(item.item_id, binId);
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  }

  const ts = () => new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');

  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <h3 className="text-sm font-bold text-gray-700 mb-1">Upload price changes</h3>
        <p className="text-xs text-gray-400 mb-3">
          CSV with columns <span className="font-mono">Zoddle_Barcode, New_price</span>. Only in-bin items are repriced &amp; moved to the retag queue; everything else is rejected.
        </p>
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv" onChange={e => setFile(e.target.files[0] || null)} className="text-sm" />
          <button onClick={handleUpload} disabled={!file || uploading}
            className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        {result && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-semibold text-zoddle-green">{result.queued}</span> repriced &amp; queued,{' '}
              <span className="font-semibold text-zoddle-pink">{result.rejected}</span> rejected.
            </p>
            <div className="flex gap-2">
              <button onClick={() => downloadCsv(`retag_stickers_${ts()}.csv`,
                  ['Item_ID', 'Brand', 'Size', 'MRP', 'OurPrice', 'Units', 'Gender', 'Discounted'], result.sticker_rows)}
                disabled={!result.sticker_rows?.length}
                className="text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                ⬇ Sticker data ({result.sticker_rows?.length || 0})
              </button>
              <button onClick={() => downloadCsv(`retag_errors_${ts()}.csv`,
                  ['zoddle_barcode', 'new_price', 'reason'], result.error_rows)}
                disabled={!result.error_rows?.length}
                className="text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                ⬇ Error list ({result.error_rows?.length || 0})
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input type="text" placeholder="Search by barcode or name..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green w-64" />
        <button onClick={load} className="text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
        <span className="ml-auto text-xs text-gray-400">{items.length} items in retag queue</span>
      </div>
      {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      : items.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">Retag queue is empty</div>
      : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Item</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Barcode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">New price</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Units</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Suggested Bin</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.item_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.image_url_1 ? <img src={cldThumb(item.image_url_1, 300)} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                      <p className="text-xs font-semibold text-gray-800">{item.design_name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.zoddle_barcode}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{item.size}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-xs text-gray-800 font-semibold">₹{item.zoddle_price}</div>
                    {item.sale_action === 'add sale' && (
                      <span className="inline-block mt-1 text-[10px] font-semibold bg-zoddle-pink/10 text-zoddle-pink px-2 py-0.5 rounded-full">add sale</span>
                    )}
                    {item.sale_action === 'remove sale' && (
                      <span className="inline-block mt-1 text-[10px] font-semibold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">remove sale</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-600">{item.units}</td>
                  <td className="px-4 py-3">
                    {item.suggested_bin ? <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{item.suggested_bin.bin_label}</span> : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleClear(item)} disabled={processing === item.item_id || !item.suggested_bin}
                      className="text-xs bg-zoddle-green text-white font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                      {processing === item.item_id ? '...' : 'Retagged → Bin'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Pre-Dispatch Tab (picking board) ──────────────────────────────────────

const PREDISPATCH_SECTIONS = [
  { key: 'not_assigned',    label: 'Not assigned',        tint: 'bg-gray-50' },
  { key: 'picking',         label: 'Picking in Progress', tint: 'bg-blue-50' },
  { key: 'needs_attention', label: 'Needs attention',     tint: 'bg-amber-50' },
  { key: 'ready',           label: 'Ready',               tint: 'bg-green-50' },
];

function ResolutionModal({ pack, onClose, onResolved }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [choices, setChoices] = useState({}); // item_id -> { action, barcode }
  const [lineErrors, setLineErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPackReview(pack.pack_id)
      .then(r => setReview(r))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pack.pack_id]);

  const notFound = (review?.items || []).filter(i => i.pick_status === 'not_found');
  const found = (review?.items || []).filter(i => i.pick_status !== 'not_found');

  function setChoice(itemId, patch) {
    setChoices(prev => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  }
  const allResolved = notFound.length > 0 && notFound.every(i => {
    const c = choices[i.item_id];
    return c && (c.action === 'ignore' || c.action === 'found' || (c.action === 'replace' && (c.barcode || '').trim()));
  });

  async function submit() {
    if (!allResolved) return;
    setBusy(true); setLineErrors({});
    const resolutions = notFound.map(i => ({
      item_id: i.item_id,
      action: choices[i.item_id].action,
      replacement_barcode: choices[i.item_id].action === 'replace' ? choices[i.item_id].barcode.trim() : null,
    }));
    try {
      const res = await resolvePack(pack.pack_id, resolutions);
      if (res.ok === false) {
        const em = {}; (res.errors || []).forEach(e => { em[e.item_id] = e.message; });
        setLineErrors(em); setBusy(false); return;
      }
      onResolved();
    } catch (e) { alert(e.response?.data?.detail || 'Resolve failed'); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Resolve pack</h2>
            <p className="text-xs text-gray-400 mt-0.5">{pack.customer_name} · <span className="font-mono">{pack.zcp_appointment_uid || '—'}</span></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? <div className="text-center py-12 text-gray-400 text-sm">Loading…</div> : (
            <>
              <p className="text-xs font-semibold text-zoddle-pink mb-2">Not found ({notFound.length}) — resolve each</p>
              <div className="space-y-2 mb-5">
                {notFound.map(it => {
                  const c = choices[it.item_id] || {};
                  return (
                    <div key={it.item_id} className="border border-pink-200 bg-pink-50/40 rounded-lg p-2.5">
                      <div className="flex items-center gap-3">
                        {it.image_url_1
                          ? <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-12 h-12 object-cover rounded bg-gray-100" />
                          : <div className="w-12 h-12 bg-gray-100 rounded" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-800 truncate">{it.brand_name || '—'}</p>
                          <p className="text-xs text-gray-400 font-mono">{it.zoddle_barcode} · {it.size}</p>
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => setChoice(it.item_id, { action: 'found' })}
                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition ${c.action === 'found' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500 hover:border-zoddle-green'}`}>Found it</button>
                          <button onClick={() => setChoice(it.item_id, { action: 'replace' })}
                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition ${c.action === 'replace' ? 'bg-zoddle-blue text-white border-zoddle-blue' : 'border-gray-200 text-gray-500 hover:border-zoddle-blue'}`}>Replace</button>
                          <button onClick={() => setChoice(it.item_id, { action: 'ignore' })}
                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition ${c.action === 'ignore' ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>Ignore</button>
                        </div>
                      </div>
                      {c.action === 'replace' && (
                        <input type="text" value={c.barcode || ''} onChange={e => setChoice(it.item_id, { barcode: e.target.value })}
                          placeholder="Replacement barcode (must be in a bin)"
                          className="mt-2 w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-zoddle-green" />
                      )}
                      {lineErrors[it.item_id] && <p className="mt-1 text-xs text-zoddle-pink">{lineErrors[it.item_id]}</p>}
                    </div>
                  );
                })}
              </div>
              {found.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-400 mb-2">Found ({found.length}) — staying in pack</p>
                  <div className="flex flex-wrap gap-1.5">
                    {found.map(it => (
                      <span key={it.item_id} className="text-xs font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 text-gray-500">{it.zoddle_barcode}</span>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-3 flex gap-3">
          <button onClick={submit} disabled={!allResolved || busy}
            className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
            {busy ? 'Resolving…' : allResolved ? 'Resolve → Ready' : 'Resolve each not-found item'}
          </button>
          <button onClick={onClose} className="border border-gray-200 text-gray-600 text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PreDispatchTab() {
  const [board, setBoard] = useState(null);
  const [packers, setPackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignSel, setAssignSel] = useState({}); // pack_id -> packer_id
  const [busy, setBusy] = useState(null);         // pack_id being assigned
  const [resolveCard, setResolveCard] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([getPredispatchBoard(), listPackers()]);
      setBoard(b); setPackers(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);

  async function assign(pack_id) {
    const packer_id = assignSel[pack_id];
    if (!packer_id) return;
    setBusy(pack_id);
    try { await assignPackToPacker(pack_id, packer_id); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(null); }
  }

  if (loading) return <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>;
  if (!board) return <div className="text-center py-16 text-gray-400 text-sm">Could not load board.</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-400">Packs ready for dispatch, by picking stage. Picking is optional — a pack can still be dispatched from the Dispatch tab.</p>
        <button onClick={load} className="text-xs font-semibold text-zoddle-green hover:underline">Refresh</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PREDISPATCH_SECTIONS.map(sec => {
          const cards = board.sections[sec.key] || [];
          return (
            <div key={sec.key} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className={`px-3 py-2 border-b border-gray-200 ${sec.tint} flex items-center justify-between`}>
                <span className="text-xs font-bold text-gray-700">{sec.label}</span>
                <span className="text-xs font-semibold text-gray-400">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[4rem]">
                {cards.length === 0 && <p className="text-xs text-gray-300 text-center py-4">—</p>}
                {cards.map(c => (
                  <div key={c.pack_id}
                    onClick={sec.key === 'needs_attention' ? () => setResolveCard(c) : undefined}
                    className={`border border-gray-200 rounded-lg p-2.5 ${sec.key === 'needs_attention' ? 'cursor-pointer hover:border-amber-400' : ''}`}>
                    <p className="text-sm font-semibold text-gray-800 truncate">{c.customer_name || '—'}</p>
                    <p className="text-xs text-gray-400">{c.scheduled_date} · <span className="font-mono">{c.zcp_appointment_uid || '—'}</span></p>
                    <p className="text-xs text-gray-500 mt-0.5">{c.item_count} items{c.assigned_packer_name ? ` · ${c.assigned_packer_name}` : ''}</p>
                    {c.issue_status === 'pack_issues' && <p className="text-xs text-zoddle-pink font-semibold mt-1">Pack issues — clean it in the Dispatch tab</p>}
                    {sec.key === 'needs_attention' && <p className="text-xs text-amber-600 font-semibold mt-1">Click to resolve →</p>}
                    {sec.key === 'picking' && (() => {
                      const a = allocatedInfo(c.allocated_at, now);
                      return a ? <p className={`text-xs mt-1 ${a.cls}`}>{a.text}</p> : null;
                    })()}
                    {sec.key === 'not_assigned' && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <select value={assignSel[c.pack_id] || ''} onChange={e => setAssignSel(s => ({ ...s, [c.pack_id]: e.target.value }))}
                          className="flex-1 min-w-0 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-zoddle-green">
                          <option value="">Assign packer…</option>
                          {packers.map(p => <option key={p.staff_id} value={p.staff_id}>{p.full_name}</option>)}
                        </select>
                        <button onClick={() => assign(c.pack_id)} disabled={!assignSel[c.pack_id] || busy === c.pack_id}
                          className="text-xs font-semibold bg-zoddle-green text-white px-2.5 py-1 rounded hover:opacity-90 disabled:opacity-40 whitespace-nowrap">
                          {busy === c.pack_id ? '…' : 'Assign'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {resolveCard && (
        <ResolutionModal pack={resolveCard}
          onClose={() => setResolveCard(null)}
          onResolved={() => { setResolveCard(null); load(); }} />
      )}
    </div>
  );
}

// ── Dispatch "Message" helper ─────────────────────────────────────────────
// Convert a stored size code (e.g. "3-4Y", "6-12M") + gender into readable form.
function readableChildSize(child) {
  const raw = (child.size || '').trim();
  const gender = child.gender === 'boy' ? 'Boy' : child.gender === 'girl' ? 'Girl' : '';
  const unit = raw.endsWith('Y') ? 'years' : raw.endsWith('M') ? 'months' : '';
  const num = unit ? raw.slice(0, -1) : raw;
  return [num && unit ? `${num} ${unit}` : raw, gender].filter(Boolean).join(' ');
}

function buildDispatchMessage(appt) {
  const sizes = (appt.children || []).map(readableChildSize).filter(Boolean);
  const sizeText = sizes.length <= 1
    ? (sizes[0] || '')
    : sizes.slice(0, -1).join(', ') + ' and ' + sizes[sizes.length - 1];
  return `Hi! This is Prakhar from Skipzy! My teammate is on his way for your kids shopping from home session. You can buy anything you like! The size you had selected was ${sizeText}.\n\n*Skipzy price match promise:* We try to keep our prices the same as or lower than other platforms. With festive sales on, prices online change daily, so if you see the same item cheaper on another website, just show us the link and we’ll match the lower price!\n\nPlease download the Skipzy app before the session for a seamless payment experience: skipzy.com/app`;
}

// ── Main OrderOps Page ────────────────────────────────────────────────────

export default function OrderOps() {
  const { auth } = useAuth();
  const { navData, setSubTab } = useNavigation();
  const isCoreTeam = auth?.access_level === 'core_team' || auth?.access_level === 'admin';
  const [tab, setTab] = useState(navData?.subTab || 'packing');
  const [packableAppts, setPackableAppts] = useState([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState('all'); // all | catalog | shop
  const [packingBookedId, setPackingBookedId] = useState(null);
  const [packAssignedAppts, setPackAssignedAppts] = useState([]);
  const [dispatchedAppts, setDispatchedAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedForPacking, setSelectedForPacking] = useState(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [dispatchPick, setDispatchPick] = useState(null);   // appointment waiting for its delivery ETA
  const [dispatchIssue, setDispatchIssue] = useState(null); // {appt, eta, issue}: dispatch refused (409) for a catalog reason
  const dispatchEtas = useDispatchEtas();
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [downloadingComicId, setDownloadingComicId] = useState(null);
  const [selectedForReversal, setSelectedForReversal] = useState(null);
  const [selectedForAdminReversal, setSelectedForAdminReversal] = useState(null);
  const [selectedForPackView, setSelectedForPackView] = useState(null);
  // Default to today's appointments (local date — en-CA formats as YYYY-MM-DD).
  const today = new Date().toLocaleDateString('en-CA');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [successMsg, setSuccessMsg] = useState(null);
  const [successAppt, setSuccessAppt] = useState(null);

  function showSuccess(msg, appt = null) {
    setSuccessMsg(msg);
    setSuccessAppt(appt);
    setTimeout(() => { setSuccessMsg(null); setSuccessAppt(null); }, 5000);
  }

  const shownPackable = packableAppts.filter(a => (
    orderTypeFilter === 'all' || (orderTypeFilter === 'catalog') === (a.order_type === 'catalog')));

  const handlePackOnlyBooked = async (a) => {
    setPackingBookedId(a.appointment_id);
    try {
      await createPackBooked(a.appointment_id);
      showSuccess('Booked pieces packed — moved to Dispatch tab');
      loadPackable();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Could not pack the booked pieces. Use Create pack.');
    } finally { setPackingBookedId(null); }
  };

  const loadPackable = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      setPackableAppts(await getPackableAppointments(params));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  const loadPackAssigned = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      setPackAssignedAppts(await getPackAssignedAppointments(params));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  const loadDispatched = useCallback(async () => {
    setLoading(true);
    try { setDispatchedAppts(await getDispatchedAppointments()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'packing') loadPackable();
    else if (tab === 'dispatch') loadPackAssigned();
    else if (tab === 'reversal' || tab === 'admin_reversal') loadDispatched();
  }, [tab, loadPackable, loadPackAssigned, loadDispatched]);

  // ⌘K deep link: navigate('orderops', { subTab }).
  useEffect(() => {
    if (navData?.subTab) setTab(navData.subTab);
  }, [navData]);

  useEffect(() => { setSubTab('orderops', tab); }, [tab, setSubTab]);

  // Deep-link from the Appointments detail modal: jump to the right tab and
  // fire the same action a click on that tab's row would (unfiltered lookup —
  // the lookups below ignore the date filter, so the target appointment can't be
  // excluded by it).
  useEffect(() => {
    if (!navData?.ooAction || !navData?.appointmentId) return;
    const { ooAction, appointmentId } = navData;
    // Drop the default today filter so the target row is visible in the list.
    setDateFrom(''); setDateTo('');
    (async () => {
      try {
        if (ooAction === 'pack') {
          setTab('packing');
          const list = await getPackableAppointments({});
          const match = list.find(a => a.appointment_id === appointmentId);
          if (match) setSelectedForPacking(match);
          else alert('This appointment is not currently awaiting packing.');
        } else if (ooAction === 'dispatch') {
          setTab('dispatch');
          const list = await getPackAssignedAppointments({});
          const match = list.find(a => a.appointment_id === appointmentId);
          if (match) handleDispatch(match);
          else alert('This appointment is not currently pack-assigned (ready to dispatch).');
        } else if (ooAction === 'reverse') {
          setTab('reversal');
          const list = await getDispatchedAppointments();
          const match = list.find(a => a.appointment_id === appointmentId);
          if (match) setSelectedForReversal(match);
          else alert('This appointment is not currently out for delivery.');
        }
      } catch (e) { alert('Failed to load this appointment for that action.'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData]);

  async function handleDispatch(appt) {
    if (!appt.pack_id) { alert('No pack found'); return; }
    if (appt.picking_status === 'needs_attention' &&
        !window.confirm('This pack still has unresolved not-found items (Needs attention in Pre-Dispatch). Dispatch anyway?')) return;
    setDispatchPick(appt);        // ETA is optional: the popup offers an ETA or "dispatch without"
  }

  async function confirmDispatch(eta, appt = dispatchPick) {   // eta may be empty: dispatch, but no WhatsApp
    setDispatchPick(null);
    setDispatchIssue(null);
    try {
      await markDispatched(appt.pack_id, eta);
      showSuccess(`${appt.customer_name} dispatched`, appt);
      loadPackAssigned();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && ['pack_issues', 'booked_pieces_in_pack'].includes(d?.code)) {
        setDispatchIssue({ appt, eta, issue: d });   // fix the box, then dispatch again
        return;
      }
      alert(typeof d === 'string' ? d : d?.message || 'Failed');
    }
  }

  // Clean a pack with issues without dispatching it (the catalog order is waiting on it).
  async function openCleanPack(appt) {
    try {
      const issue = await getPackIssues(appt.pack_id);
      if (!issue?.items?.length) { loadPackAssigned(); return; }
      setDispatchIssue({ appt, issue, cleanOnly: true });
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not load the pack issues.');
    }
  }

  async function handleCopyMessage(appt) {
    try {
      await navigator.clipboard.writeText(buildDispatchMessage(appt));
      setCopiedMsgId(appt.appointment_id);
      setTimeout(() => setCopiedMsgId(null), 2000);
    } catch (e) { alert('Could not copy to clipboard'); }
  }

  async function handleDownloadComic(appt) {
    setDownloadingComicId(appt.appointment_id);
    try {
      await downloadComicGift(appt.appointment_id, appt.zcp_appointment_uid);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not download comic gift');
    } finally {
      setDownloadingComicId(null);
    }
  }

  async function handleCancelPack(appt) {
    if (!appt.pack_id) { alert('No pack found'); return; }
    try {
      await cancelPack(appt.pack_id);
      showSuccess('Pack cancelled — return items to the restocking bin');
      loadPackAssigned();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  }

  const TABS = [
    { key: 'packing', label: 'Packing' },
    { key: 'predispatch', label: 'Pre-Dispatch' },
    { key: 'dispatch', label: 'Dispatch' },
    { key: 'reversal', label: 'Reversal' },
    { key: 'admin_reversal', label: 'Admin Reversal' },
    { key: 'restock', label: 'Restock' },
    { key: 'ironing', label: 'Ironing' },
    { key: 'retagging', label: 'Retagging' },
    ...(isCoreTeam ? [{ key: 'search', label: 'Search' }, { key: 'receipt', label: 'Generate Invoice' }, { key: 'pricematch', label: 'Price Match' }, { key: 'courier', label: 'Catalog — no rider' }, { key: 'slots', label: 'Slots Manage' }] : []),
  ];

  function DateFilter({ onApply }) {
    return (
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
        </div>
        <button onClick={onApply} className="border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition">Apply</button>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-gray-400 hover:text-zoddle-pink transition">Clear</button>
        )}
      </div>
    );
  }

  function ReversalTable({ appointments, onAction, actionLabel, actionClass }) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">UID</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Dispatched At</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((a, i) => (
              <tr key={a.appointment_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-800">{a.scheduled_date}</p>
                  <p className="text-xs text-gray-400">{a.scheduled_time}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{a.customer_name || '—'}</p>
                  <p className="text-xs text-gray-400">{a.phone}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs text-gray-600">{a.zcp_appointment_uid || '—'}</span>
                  <PackOriginNote origin={a.pack_origin} className="mt-1" />
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{a.dispatched_at?.slice(0,16).replace('T',' ') || '—'}</td>
                <td className="px-4 py-3">
                  <button onClick={() => onAction(a)} className={`text-xs font-semibold hover:underline whitespace-nowrap ${actionClass}`}>
                    {actionLabel}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-gray-800">Order Ops</h1>
        <p className="text-sm text-gray-400 mt-0.5">Packing, dispatch, reversal and condition processing</p>
      </div>

      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 mb-4 text-sm text-green-700 font-medium flex items-center gap-2">
          <span>✓ {successMsg}</span>
          {successAppt && (
            <button onClick={() => handleCopyMessage(successAppt)} title="Copy customer message"
              className="text-zoddle-blue hover:text-zoddle-green transition">
              {copiedMsgId === successAppt.appointment_id ? (
                <span className="text-xs font-semibold text-zoddle-green">Copied!</span>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none"
                     viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <rect x="9" y="9" width="11" height="11" rx="2" />
                  <path d="M5 15V5a2 2 0 012-2h10" />
                </svg>
              )}
            </button>
          )}
        </div>
      )}

      <div className="flex gap-6 border-b border-gray-200 mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`pb-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? 'border-zoddle-green text-zoddle-green' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Pre-Dispatch Tab */}
      {tab === 'predispatch' && <PreDispatchTab />}

      {/* Packing Tab */}
      {tab === 'packing' && (
        <div>
          <DateFilter onApply={loadPackable} />
          <div className="flex items-center gap-3 mb-3">
            <p className="text-xs text-gray-400">{shownPackable.length} appointments awaiting packing</p>
            <select value={orderTypeFilter} onChange={e => setOrderTypeFilter(e.target.value)}
              className="ml-auto border border-gray-300 rounded-lg px-2 py-1 text-xs">
              <option value="all">All orders</option>
              <option value="catalog">Catalog only</option>
              <option value="shop">Shop at home only</option>
            </select>
          </div>
          {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          : shownPackable.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No appointments awaiting packing</div>
          : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date & Time</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">UID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Children</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {shownPackable.map((a, i) => (
                    <tr key={a.appointment_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800 whitespace-nowrap">{a.scheduled_date}</p>
                        <p className="text-xs text-gray-400 whitespace-nowrap">{a.order_type === 'catalog' ? 'ASAP' : a.scheduled_time}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{a.customer_name || '—'}</p>
                        <p className="text-xs text-gray-400">{a.phone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600">{a.zcp_appointment_uid || '—'}</span>
                        {a.order_type === 'catalog' && (
                          <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-zoddle-blue/15 text-zoddle-blue">Catalog</span>
                        )}
                        {a.booking_changed && (
                          <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title="A booked piece was sold or became unavailable">Booking changed</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.children?.map((c, ci) => <p key={ci} className="text-xs text-gray-600">{c.size} | {c.gender}</p>)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3 items-center">
                          {a.order_type === 'catalog' && (
                            <button onClick={() => handlePackOnlyBooked(a)} disabled={packingBookedId === a.appointment_id || a.booking_changed}
                              title={a.booking_changed ? 'A booked piece changed: use Create pack to review' : 'Pack exactly the booked pieces'}
                              className="text-xs text-zoddle-blue font-semibold hover:underline disabled:opacity-40 disabled:no-underline">
                              {packingBookedId === a.appointment_id ? 'Packing…' : 'Pack only booked'}
                            </button>
                          )}
                          <button onClick={() => setSelectedForPacking(a)}
                            className="text-xs text-zoddle-green font-semibold hover:underline">{a.order_type === 'catalog' ? 'Create pack' : 'Pack Items'}</button>
                          {a.has_comic && (
                            <button onClick={() => handleDownloadComic(a)} title="Comic gift — click to download"
                              disabled={downloadingComicId === a.appointment_id}
                              className="text-red-500 hover:text-red-700 transition disabled:opacity-40">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 2L1 21h22L12 2zm0 6a1 1 0 011 1v5a1 1 0 01-2 0V9a1 1 0 011-1zm0 9a1.25 1.25 0 100 2.5A1.25 1.25 0 0012 17z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Dispatch Tab */}
      {tab === 'dispatch' && (
        <div>
          <DateFilter onApply={loadPackAssigned} />
          <p className="text-xs text-gray-400 mb-3">{packAssignedAppts.length} packs ready to dispatch</p>
          {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          : packAssignedAppts.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No packs ready to dispatch</div>
          : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date & Time</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">UID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Children</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Items</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {packAssignedAppts.map((a, i) => (
                    <tr key={a.appointment_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800 whitespace-nowrap">{a.scheduled_date}</p>
                        <p className="text-xs text-gray-400 whitespace-nowrap">{a.scheduled_time}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{a.customer_name || '—'}</p>
                        <p className="text-xs text-gray-400">{a.phone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600">{a.zcp_appointment_uid || '—'}</span>
                        {a.issue_status === 'pack_issues' && (
                          <button type="button" onClick={() => openCleanPack(a)}
                            title="A piece in this box is booked for a catalog order. Clean the pack before it can be dispatched."
                            className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-zoddle-pink/15 text-zoddle-pink hover:bg-zoddle-pink/25">Pack issues · Clean pack</button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.children?.map((c, ci) => <p key={ci} className="text-xs text-gray-600">{c.size} | {c.gender}</p>)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {a.total_items_packed > 0 ? (
                          <button onClick={() => setSelectedForPackView(a)}
                                  className="text-zoddle-blue font-semibold hover:underline">
                            {a.total_items_packed}
                          </button>
                        ) : (a.total_items_packed ?? '—')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3 items-center">
                          <SetsPill packId={a.pack_id} />
                          <button onClick={() => handleDispatch(a)}
                            className="text-xs text-zoddle-green font-semibold hover:underline">Dispatch</button>
                          <button onClick={() => a.pack_id ? setSelectedForEdit(a) : alert('No pack found')}
                            className="text-xs text-zoddle-blue font-semibold hover:underline">Edit</button>
                          <button onClick={() => handleCancelPack(a)}
                            className="text-xs text-zoddle-pink font-semibold hover:underline">Cancel Pack</button>
                          <button onClick={() => handleCopyMessage(a)} title="Copy customer message"
                            className="text-zoddle-blue hover:text-zoddle-green transition">
                            {copiedMsgId === a.appointment_id ? (
                              <span className="text-xs font-semibold text-zoddle-green">Copied!</span>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none"
                                   viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <rect x="9" y="9" width="11" height="11" rx="2" />
                                <path d="M5 15V5a2 2 0 012-2h10" />
                              </svg>
                            )}</button>
                          {a.has_comic && (
                            <button onClick={() => handleDownloadComic(a)} title="Comic gift — click to download"
                              disabled={downloadingComicId === a.appointment_id}
                              className="text-red-500 hover:text-red-700 transition disabled:opacity-40">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 2L1 21h22L12 2zm0 6a1 1 0 011 1v5a1 1 0 01-2 0V9a1 1 0 011-1zm0 9a1.25 1.25 0 100 2.5A1.25 1.25 0 0012 17z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reversal Tab */}
      {tab === 'reversal' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-400">{dispatchedAppts.length} appointments out for delivery</p>
            <button onClick={loadDispatched} className="text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
          </div>
          {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          : dispatchedAppts.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No appointments currently dispatched</div>
          : <ReversalTable appointments={dispatchedAppts} onAction={a => setSelectedForReversal(a)}
              actionLabel="Start Reversal" actionClass="text-zoddle-pink" />}
        </div>
      )}

      {/* Admin Reversal Tab */}
      {tab === 'admin_reversal' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-400">{dispatchedAppts.length} appointments out for delivery</p>
            <button onClick={loadDispatched} className="text-xs text-gray-400 hover:text-zoddle-green transition">↻ Refresh</button>
          </div>
          {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          : dispatchedAppts.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No appointments currently dispatched</div>
          : <ReversalTable appointments={dispatchedAppts} onAction={a => setSelectedForAdminReversal(a)}
              actionLabel="Admin Review" actionClass="text-zoddle-blue" />}
        </div>
      )}

      {tab === 'restock' && <RestockTab />}
      {tab === 'ironing' && <IroningTab />}
      {tab === 'retagging' && <RetaggingTab />}
      {tab === 'search' && isCoreTeam && <SearchQueueTab />}
      {tab === 'slots' && isCoreTeam && <SlotsManageTab />}
      {tab === 'pricematch' && isCoreTeam && <PriceMatchTab />}
      {tab === 'courier' && isCoreTeam && <CatalogCourierTab />}

      {/* Generate Invoice Tab */}
      {tab === 'receipt' && isCoreTeam && <GenerateReceiptTab />}

      {selectedForPacking && (
        <ItemPickerModal appointment={selectedForPacking}
          onClose={() => setSelectedForPacking(null)}
          onPacked={() => { setSelectedForPacking(null); showSuccess('Pack created — moved to Dispatch tab'); loadPackable(); }} />
      )}
      {selectedForEdit && (
        <ItemPickerModal appointment={selectedForEdit} editPackId={selectedForEdit.pack_id}
          onClose={() => setSelectedForEdit(null)}
          onPacked={() => { setSelectedForEdit(null); showSuccess('Pack updated'); loadPackAssigned(); }} />
      )}
      {dispatchPick && (
        <Modal title={`Dispatch ${dispatchPick.customer_name || ''}`.trim()} onClose={() => setDispatchPick(null)}>
          <p className="text-sm text-gray-600 mb-3">
            Pick a delivery ETA to tell the customer when it will arrive; the pack is dispatched as soon as you choose.
            If you are not sure of the time, dispatch without it and no ETA message is sent.
          </p>
          <select defaultValue="" autoFocus
            onChange={e => e.target.value && confirmDispatch(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green">
            <option value="" disabled>Select ETA…</option>
            {dispatchEtas.map(eta => <option key={eta} value={eta}>{eta}</option>)}
          </select>
          <button onClick={() => confirmDispatch('')}
            className="mt-3 w-full text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
            Dispatch without ETA (no message sent)
          </button>
        </Modal>
      )}
      {dispatchIssue && (
        <DispatchIssueModal appt={dispatchIssue.appt} issue={dispatchIssue.issue} cleanOnly={!!dispatchIssue.cleanOnly}
          onClose={() => { setDispatchIssue(null); loadPackAssigned(); }}
          onResolved={dispatchIssue.cleanOnly
            ? () => { setDispatchIssue(null); loadPackAssigned(); }
            : () => confirmDispatch(dispatchIssue.eta, dispatchIssue.appt)} />
      )}
      {selectedForPackView && (
        <PackItemsModal appointment={selectedForPackView}
          onClose={() => setSelectedForPackView(null)} />
      )}
      {selectedForReversal && (
        <ReversalModal appointment={selectedForReversal}
          onClose={() => setSelectedForReversal(null)}
          onComplete={result => {
            setSelectedForReversal(null);
            if (result === 'success') showSuccess('Items received. Please put all items in the restocking bin.');
            loadDispatched();
          }} />
      )}
      {selectedForAdminReversal && (
        <AdminReversalModal appointment={selectedForAdminReversal}
          onClose={() => setSelectedForAdminReversal(null)}
          onComplete={() => { setSelectedForAdminReversal(null); showSuccess('Admin reversal complete — items moved to restock queue'); loadDispatched(); }} />
      )}
    </div>
  );
}
